import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { supabase } from '../lib/supabase'

const { FiClock, FiMapPin, FiPhone, FiPackage } = FiIcons

const PublicMenu = () => {
  const [platos, setPlatos] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [isWeekend, setIsWeekend] = useState(false)
  
  const [menuConfig, setMenuConfig] = useState({
    showMenuLegend: true,
    menuPrice: 13.00,
    menuTitle: 'MENÚ DEL DÍA',
    menuDescription: 'Incluye primer plato, segundo plato, pan y postre',
    showFestiveBanner: false,
    festiveImageUrl: '',
    festiveAltText: 'Imagen festiva',
    festiveEndDate: ''
  })

  useEffect(() => {
    const dayOfWeek = currentDate.getDay()
    setIsWeekend(dayOfWeek === 0 || dayOfWeek === 6) // Sunday=0, Saturday=6
    
    fetchTodaysMenu()
    fetchMenuConfig()
  }, [currentDate])

  const fetchMenuConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_config')
        .select('*')
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching menu config:', error)
      } else if (data) {
        setMenuConfig({
          showMenuLegend: data.show_menu_legend,
          menuPrice: data.menu_price,
          menuTitle: data.menu_title || 'MENÚ DEL DÍA',
          menuDescription: data.menu_description || 'Incluye primer plato, segundo plato, pan y postre',
          showFestiveBanner: data.show_festive_banner || false,
          festiveImageUrl: data.festive_image_url || '',
          festiveAltText: data.festive_alt_text || 'Imagen festiva',
          festiveEndDate: data.festive_end_date || ''
        })
      }
    } catch (error) {
      console.error('Error fetching menu config:', error)
    }
  }

  const fetchTodaysMenu = async () => {
    try {
      setLoading(true)
      const today = format(currentDate, 'yyyy-MM-dd')
      
      const { data, error } = await supabase
        .from('platos')
        .select('*')
        .eq('fecha', today)
        .eq('visible', true)
        .order('categoria')
        .order('orden')

      if (error) {
        console.error('Error fetching menu:', error)
        setPlatos([])
      } else {
        console.log('Fetched platos:', data) // Debug log
        setPlatos(data || [])
      }
    } catch (error) {
      console.error('Error fetching menu:', error)
      setPlatos([])
    } finally {
      setLoading(false)
    }
  }

  // Función para capitalizar solo el día y el mes (nombres propios)
  const capitalizeProperNouns = (str) => {
    return str.replace(
      /\b(lunes|martes|miércoles|jueves|viernes|sábado|domingo|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/gi,
      (match) => {
        return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
      }
    )
  }

  const groupedPlatos = platos.reduce((acc, plato) => {
    if (!acc[plato.categoria]) {
      acc[plato.categoria] = []
    }
    acc[plato.categoria].push(plato)
    return acc
  }, {})

  const weekdayCategories = ['primero', 'segundo']
  const weekdayExtraCategories = ['tipico', 'parrilla']
  const weekendCategories = ['tipico', 'parrilla']
  const drinkCategories = ['bebida_casa', 'bebida_botella', 'cerveza']

  const categoryNames = {
    primero: 'PRIMEROS PLATOS',
    segundo: 'SEGUNDOS PLATOS',
    tipico: 'PLATOS TÍPICOS BOLIVIANOS',
    parrilla: 'PARRILLA',
    bebida_casa: 'BEBIDAS DE LA CASA',
    bebida_botella: 'BEBIDAS DE BOTELLA',
    cerveza: 'CERVEZAS'
  }

  const allergenColors = {
    gluten: 'bg-yellow-100 text-yellow-800',
    lacteos: 'bg-blue-100 text-blue-800',
    huevos: 'bg-orange-100 text-orange-800',
    frutos_secos: 'bg-red-100 text-red-800',
    pescado: 'bg-cyan-100 text-cyan-800',
    sulfitos: 'bg-purple-100 text-purple-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vip-orange mx-auto mb-4"></div>
          <p className="text-vip-brown">Cargando menú...</p>
        </div>
      </div>
    )
  }

  const renderMenuSection = (categories, showPrice = true, centerItems = false, showCategoryTitle = true) => {
    return categories.map((categoria) => {
      const platosCategoria = groupedPlatos[categoria]
      
      if (!platosCategoria || platosCategoria.length === 0) return null

      return (
        <motion.section 
          key={categoria}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: categories.indexOf(categoria) * 0.1 }}
          className="mb-8"
        >
          {showCategoryTitle && (
            <h2 className="text-2xl md:text-3xl font-bold text-vip-orange mb-4 text-center">
              {categoryNames[categoria]}
            </h2>
          )}
          <div className={`grid gap-3 md:gap-4 ${centerItems ? 'max-w-2xl mx-auto' : ''}`}>
            {platosCategoria.map((plato, index) => (
              <motion.div
                key={plato.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 border-l-4 border-vip-orange"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-vip-brown mb-1">
                      {plato.nombre}
                    </h3>
                    {plato.descripcion && (
                      <p className="text-gray-600 mb-2 leading-relaxed">
                        {plato.descripcion}
                      </p>
                    )}
                    {plato.alergenos && plato.alergenos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="text-sm text-gray-500 mr-2">Alérgenos:</span>
                        {plato.alergenos.map((alergeno) => (
                          <span 
                            key={alergeno}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${allergenColors[alergeno] || 'bg-gray-100 text-gray-800'}`}
                          >
                            {alergeno}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex flex-col items-end">
                    {showPrice && plato.precio && (
                      <span className="text-2xl font-bold text-vip-dark">
                        {plato.precio.toFixed(2)}€
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )
    })
  }

  const hasMenuItems = (weekdayCategories.some(cat => groupedPlatos[cat] && groupedPlatos[cat].length > 0))
  const hasWeekdayExtraItems = (weekdayExtraCategories.some(cat => groupedPlatos[cat] && groupedPlatos[cat].length > 0))
  const hasWeekendItems = (weekendCategories.some(cat => groupedPlatos[cat] && groupedPlatos[cat].length > 0))
  const hasDrinkItems = (drinkCategories.some(cat => groupedPlatos[cat] && groupedPlatos[cat].length > 0))

  // Calculamos si el banner festivo debe mostrarse teniendo en cuenta la fecha de expiración
  const todayStr = format(currentDate, 'yyyy-MM-dd')
  const isBannerActive = menuConfig.showFestiveBanner && 
                         menuConfig.festiveImageUrl && 
                         (!menuConfig.festiveEndDate || todayStr <= menuConfig.festiveEndDate)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-2 border-vip-orange">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-vip-orange mb-2"
            >
              VIP – BAR
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-semibold text-vip-brown mb-2"
            >
              CARTA – MENÚ
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-vip-brown text-lg"
            >
              {capitalizeProperNouns(format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es }))}
            </motion.p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Festive Banner - Muestra solo si está activo y no ha expirado */}
        {isBannerActive && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="bg-white rounded-lg p-6 text-center shadow-lg border border-gray-200">
              <img 
                src={menuConfig.festiveImageUrl}
                alt={menuConfig.festiveAltText}
                className="max-h-48 mx-auto rounded-lg shadow-md"
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.nextElementSibling.style.display = 'block'
                }}
              />
              <div className="text-gray-500 text-lg font-bold mt-2 hidden">
                🎉 ¡Día Especial! 🎉
              </div>
            </div>
          </motion.div>
        )}

        {/* Menu del Día Legend - Solo en días de semana */}
        {!isWeekend && hasMenuItems && menuConfig.showMenuLegend && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-vip-orange to-vip-dark text-white rounded-lg p-6 mb-6 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-2">{menuConfig.menuTitle}</h2>
            <p className="text-xl font-semibold text-orange-100">
              {menuConfig.menuPrice.toFixed(2)}€
            </p>
            <p className="text-orange-200 text-sm mt-1">
              {menuConfig.menuDescription}
            </p>
          </motion.div>
        )}

        {/* Weekday Menu Sections - Siempre mostrar títulos de categorías */}
        {!isWeekend && hasMenuItems && (
          <>
            {renderMenuSection(weekdayCategories, false, true, true)}
          </>
        )}

        {/* Weekday Extra Categories */}
        {!isWeekend && hasWeekdayExtraItems && (
          <>
            {renderMenuSection(weekdayExtraCategories, true, false, true)}
          </>
        )}

        {/* Weekend Menu Sections */}
        {isWeekend && hasWeekendItems && (
          <>
            {renderMenuSection(weekendCategories, true, false, true)}
          </>
        )}

        {/* Drinks Section - Always Available */}
        {hasDrinkItems && (
          <>
            {renderMenuSection(drinkCategories, true, false, true)}
          </>
        )}

        {/* No Menu Available Message */}
        {!hasMenuItems && !hasWeekendItems && !hasDrinkItems && !hasWeekdayExtraItems && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <SafeIcon icon={FiClock} className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-vip-brown mb-2">
              No hay menú disponible
            </h3>
            <p className="text-gray-600">
              El menú de hoy aún no está disponible. Vuelve más tarde.
            </p>
          </motion.div>
        )}

        {/* Nota de Envases - REDUCIDA 60% la distancia al footer */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 mb-3"
        >
           <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg p-6 shadow-lg border-2 border-amber-400">
            <div className="flex items-center justify-center mb-3">
              <SafeIcon icon={FiPackage} className="text-3xl mr-3" />
              <h3 className="text-xl md:text-2xl font-bold">NOTA IMPORTANTE</h3>
            </div>
            <p className="text-center text-lg md:text-xl font-semibold leading-relaxed">
              Estimado cliente, los envases para llevar tienen un costo, consulte a su camarero
            </p>
          </div>
        </motion.div>

      </main>

      {/* Footer */}
      <footer className="bg-vip-dark text-white py-8 mt-3">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <SafeIcon icon={FiMapPin} className="text-xl mx-auto mb-2" />
              <p className="text-sm">Calle de Almendrales, 19<br/>Usera, 28026, Madrid</p>
            </div>
            <div>
              <SafeIcon icon={FiPhone} className="text-xl mx-auto mb-2" />
              <p className="text-sm">910 52 34 50</p>
            </div>
            <div>
              <SafeIcon icon={FiClock} className="text-xl mx-auto mb-2" />
              <p className="text-sm">Lun-Dom: 13:00 - 24:00</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PublicMenu